import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react3'
import {hiddenMessages} from './diagnostics'

/** Where a row about one viewport's rendering is allowed to show. */
const VIEWPORT_DISPLAY_MAP = {
    mobile: {base: 'block', s: 'none'},
    desktop: {base: 'none', s: 'block'}
}

/**
 * Tones: a payload that cannot render, against a layout worth knowing about.
 *
 * Drawn outside the design system — raw values, and an amber the palette has no
 * name for — so a notice cannot be read as content. Each tone carries the mark
 * its rows open with, so severity survives being copied out or read without
 * colour.
 */
const TONE_MAP = {
    issue: {
        mark: '⚠',
        bg: '#fdf0d5',
        color: '#5a4200'
    },
    info: {
        mark: '※',
        bg: 'border.subtle',
        color: 'inherit'
    }
}

/**
 * Everything this pane has to tell the editor, in one block above it — one
 * block rather than a box per message, since the flyout's `container` puts its
 * 32px column gap between every child.
 *
 * A row tied to a viewport shows only where it holds: an entry taken off by its
 * `hide` flag is true at one width and wrong at the other. A pane with nothing
 * to say renders no chrome at all.
 *
 * @component
 * @param {Object} props
 * @param {string[]} [props.issues] - Payload problems, shown at both viewports.
 * @param {{mobile: string[], desktop: string[]}} [props.hidden] - Labels of the
 *   entries each viewport hides, from `hiddenLabels`.
 * @param {Array<{message: string, viewport?: 'mobile'|'desktop'}>} [props.info] -
 *   Anything else worth knowing, such as a column only one viewport shows.
 * @returns {JSX.Element|null}
 */
export const PreviewPaneNotices = ({issues = [], hidden, info = []}) => {
    const rows = [
        ...issues.map((message) => ({message, tone: 'issue'})),
        ...info,
        ...hiddenMessages(hidden?.mobile ?? []).map((message) => ({message, viewport: 'mobile'})),
        ...hiddenMessages(hidden?.desktop ?? []).map((message) => ({message, viewport: 'desktop'}))
    ]

    if (!rows.length) return null

    // A block whose every row belongs to the other viewport still lays out —
    // and still applies the pull-up below, which would drag the pane content up
    // into the frame's inset. So the block goes where its rows go.
    const shows = (viewport) =>
        rows.some((row) => !row.viewport || row.viewport === viewport) ? 'flex' : 'none'

    return (
        <Box
            css={{
                display: {base: shows('mobile'), s: shows('desktop')},
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '1px',
                // Pulled up against the frame's top border, out of the content
                // inset the pane itself needs: a caption belongs on the edge it
                // annotates, and the space then reads as separating it from the
                // pane below rather than floating it between the two. The inset
                // is read back from the flyout's own custom property, so this
                // holds at both viewports, where it differs.
                marginBlockStart: 'calc({spacing.spacing-8} - var(--flyout-scroll-inset, 0px))'
            }}
        >
            {rows.map(({message, tone = 'info', viewport}, idx) => {
                const {mark, ...toneStyles} = TONE_MAP[tone]
                return (
                    // keyed by position: two entries authored under the same
                    // title produce the same message
                    <Box
                        key={idx}
                        css={[
                            {
                                display: 'block',
                                paddingInline: 'spacing-4',
                                // the tag's size, so the chrome reads as one thing
                                fontSize: '10px',
                                lineHeight: 1.6,
                                ...toneStyles
                            },
                            viewport && {display: VIEWPORT_DISPLAY_MAP[viewport]}
                        ]}
                    >
                        {`${mark} ${message}`}
                    </Box>
                )
            })}
        </Box>
    )
}

PreviewPaneNotices.propTypes = {
    issues: PropTypes.arrayOf(PropTypes.string),
    hidden: PropTypes.shape({
        mobile: PropTypes.arrayOf(PropTypes.string),
        desktop: PropTypes.arrayOf(PropTypes.string)
    }),
    info: PropTypes.arrayOf(
        PropTypes.shape({
            message: PropTypes.string.isRequired,
            viewport: PropTypes.oneOf(['mobile', 'desktop'])
        })
    )
}
